/**
 * p5.js runner.
 *
 * Replaces the p5 web editor the `js_game` graph was written against, which is the
 * whole point: the learner stopped having to copy their sketch into another tab to
 * see it move, and the tutor stopped being blind to what they wrote. Companion
 * mode was an honest seam, not a good one — a game domain where the game runs
 * somewhere else asks a child to hold two windows in their head before they have
 * held a for-loop in it.
 *
 * **The sketch runs in an iframe** (`public/p5-sandbox.html`), one fresh document
 * per run. Two reasons, in order of importance:
 *
 *  1. A run must not inherit anything from the last one. `let score = 0` evaluated
 *     twice in one realm is a redeclaration error, and a sketch that only works
 *     because a variable survived from the previous attempt is the exact bug the
 *     Python runner's fresh namespace exists to prevent.
 *  2. Stopping is then total: no orphaned timers, no second p5 instance drawing
 *     over the first, no audio context left holding the speakers.
 *
 * **It is deliberately a same-origin frame, not `sandbox="allow-scripts"`.** The
 * isolation that matters here is between one run and the next, which a fresh
 * document already gives. An opaque origin would cost three things this domain
 * actually uses: a user gesture no longer propagates from the parent, so
 * `loadSound()` would meet the autoplay policy with nothing to satisfy it;
 * `localStorage` throws rather than works; and CSP `'self'` in a sandboxed document
 * is a corner browsers have disagreed about. The learner's own sketch is not the
 * threat model — the Strudel runner already evaluates learner code on the page
 * itself.
 *
 * **A runaway `while (true)` still freezes the tab**, because a same-origin frame
 * shares the main thread. That is what the p5 web editor does too, the editor
 * content is saved server-side before it can happen, and a refresh recovers it —
 * but nothing here can stop it, and pretending otherwise with a watchdog that also
 * cannot run would be worse. `draw()` itself is safe: it yields between frames by
 * design, which is why the Pyodide runner's ten-second kill makes no sense here.
 * See decisions.md D42.
 */

import type { Runner, RunResult } from "./types";
import { truncateDetail } from "./types";

/** Tag on every message in both directions. Must match `public/p5-sandbox.js`. */
export const SANDBOX_HOST = "qrl-p5";
export const SANDBOX_URL = "/p5-sandbox.html";

/**
 * How long a running sketch is watched before the run is reported.
 *
 * Long enough for setup plus roughly a second of frames, so a sketch that throws
 * on frame one is reported as an error rather than as "running"; short enough that
 * Run still feels like a button. Errors resolve the run immediately, so this is the
 * cost of success, not of failure.
 */
export const SETTLE_MS = 900;

/** How long the sketch page gets to load before we say something is wrong. */
export const START_TIMEOUT_MS = 8_000;

export interface ConsoleLine {
  level: string;
  text: string;
}

export interface SketchReport {
  /** `frameCount` when the report was taken. */
  frames: number;
  canvas: { width: number; height: number } | null;
  looping: boolean;
  lines: ConsoleLine[];
  /** Console lines past the cap, counted but not kept. */
  dropped: number;
  error: string | null;
}

export type SandboxMessage =
  | { type: "ready" }
  | { type: "started"; drawing: boolean }
  | { type: "error"; message: string }
  | ({ type: "report" } & SketchReport);

export type HostMessage =
  | { type: "run"; code: string }
  | { type: "report" }
  | { type: "stop" };

/**
 * The sketch page, as the runner needs it.
 *
 * Injected rather than reached for, the same way `StrudelRunner` takes a loader:
 * everything worth testing here is about what the runner does with what comes back,
 * and none of it should need a browser to test.
 */
export interface SketchHost {
  /** Replace any current document with a fresh one; resolves when it is ready. */
  start(onMessage: (message: SandboxMessage) => void): Promise<void>;
  send(message: HostMessage): void;
  /** Remove the document entirely. */
  destroy(): void;
}

export const P5_STARTER = `// Press Run. Change one number. Press Run again.
function setup() {
  createCanvas(400, 400);
}

function draw() {
  background(20, 24, 32);
  fill(120, 200, 255);
  circle(mouseX, mouseY, 40);
}`;

/**
 * Collapse runs of the same line into one with a count.
 *
 * `console.log(x)` inside `draw()` is the most useful debugging move in this graph
 * and prints sixty times a second. Sixty identical lines tell the tutor nothing the
 * first one did not, and cost real money in the prompt; "× 57" tells it more.
 */
export function collapseRepeats(lines: ConsoleLine[]): string[] {
  const out: string[] = [];
  let last: string | null = null;
  let count = 0;
  const flush = () => {
    if (last === null) return;
    out.push(count > 1 ? `${last}   × ${count}` : last);
  };
  for (const line of lines) {
    const text = line.level === "log" || line.level === "info" ? line.text : `[${line.level}] ${line.text}`;
    if (text === last) {
      count += 1;
      continue;
    }
    flush();
    last = text;
    count = 1;
  }
  flush();
  return out;
}

function consoleBlock(report: SketchReport): string {
  const collapsed = collapseRepeats(report.lines);
  if (collapsed.length === 0 && report.dropped === 0) return "";
  const tail =
    report.dropped > 0
      ? [`… ${report.dropped.toLocaleString()} more console lines not kept`]
      : [];
  return [...collapsed, ...tail].join("\n");
}

/** Turn a settled report into what the learner and the tutor both read. */
export function describeRun(report: SketchReport, drawing: boolean): RunResult {
  const output = consoleBlock(report);

  if (report.error) {
    return {
      outcome: "error",
      detail: truncateDetail(output ? `${report.error}\n\n${output}` : report.error),
    };
  }

  if (!drawing) {
    // Plain JavaScript, no sketch: the same two outcomes the Python runner uses,
    // because here the printed output really is the whole result.
    return {
      outcome: output ? "ok" : "silent",
      detail: truncateDetail(
        output ||
          "It ran. Nothing was drawn and nothing was printed — there is no setup() or " +
            "draw() in this code, and nothing called console.log().",
      ),
    };
  }

  const size = report.canvas ? `${report.canvas.width}×${report.canvas.height}` : "no";
  let headline: string;
  if (report.frames === 0) {
    // Almost always preload(): loadImage and loadSound hold setup() back until the
    // file arrives, and on a slow connection that outlasts the settle window.
    headline =
      `The sketch started on a ${size} canvas but has not drawn a frame yet. If it is ` +
      "loading an image or a sound, it may still be fetching one.";
  } else if (!report.looping) {
    headline =
      `The sketch drew ${report.frames} frame${report.frames === 1 ? "" : "s"} on a ${size} ` +
      "canvas and then stopped — draw() is not looping (noLoop, or no draw function).";
  } else {
    headline = `The sketch is running on a ${size} canvas — ${report.frames} frames so far.`;
  }

  return {
    outcome: "drawing",
    detail: truncateDetail(output ? `${headline}\n\n${output}` : headline),
  };
}

export class P5Runner implements Runner {
  readonly id = "p5";
  readonly language = "javascript";
  readonly runLabel = "Run";
  readonly starterCode = P5_STARTER;
  readonly canStop = true;
  readonly liveLabel = "running";
  readonly needsSurface = true;

  private surface: HTMLElement | null = null;
  private host: SketchHost | null = null;
  private settled = false;
  /** Told about an error that arrives after the run was reported. */
  onLateError: ((message: string) => void) | null = null;

  constructor(
    private readonly createHost: (surface: HTMLElement) => SketchHost,
    private readonly settleMs = SETTLE_MS,
    private readonly startTimeoutMs = START_TIMEOUT_MS,
  ) {}

  attachSurface(surface: HTMLElement | null): void {
    if (surface === this.surface) return;
    this.host?.destroy();
    this.host = null;
    this.surface = surface;
  }

  async load(): Promise<void> {
    // Nothing to warm up: the sketch page is a static file and gets built fresh on
    // every run anyway. p5 itself is served from our own origin and cached.
  }

  async run(code: string): Promise<RunResult> {
    if (!this.surface) {
      return { outcome: "error", detail: "The sketch area is not on screen yet." };
    }
    if (!this.host) this.host = this.createHost(this.surface);
    const host = this.host;

    let resolve: ((result: RunResult) => void) | null = null;
    const finish = (result: RunResult) => {
      const settle = resolve;
      resolve = null;
      this.settled = true;
      settle?.(result);
    };

    const finished = new Promise<RunResult>((r) => {
      resolve = r;
    });

    let drawing = false;
    let started = false;
    this.settled = false;

    const onMessage = (message: SandboxMessage) => {
      switch (message.type) {
        case "started":
          started = true;
          drawing = message.drawing;
          // A sketch is watched for a moment; plain JavaScript has already finished
          // by the time this arrives, so it is asked for its report straight away.
          if (drawing) {
            setTimeout(() => host.send({ type: "report" }), this.settleMs);
          } else {
            host.send({ type: "report" });
          }
          break;
        case "error":
          // An error after the sketch started arrives with the report as well, so
          // the console output goes with it; this only has to handle the two ends.
          if (this.settled) this.onLateError?.(message.message);
          // Failed before p5 was ever asked to start — a syntax error, or something
          // throwing at the top level. The report is asked for immediately rather
          // than the message being used as-is, because whatever the code managed to
          // print before it died is often the more useful half.
          else if (!started) host.send({ type: "report" });
          break;
        case "report":
          finish(describeRun(message, drawing));
          break;
        case "ready":
          break;
      }
    };

    try {
      await this.withTimeout(host.start(onMessage), this.startTimeoutMs);
    } catch {
      return {
        outcome: "error",
        detail:
          "The sketch page did not load. If this keeps happening, the app's " +
          "Content-Security-Policy may be blocking it — a facilitator should check " +
          "that /p5-sandbox.html and /p5/p5.min.js are being served (see " +
          "deploy/Caddyfile).",
      };
    }

    host.send({ type: "run", code });

    // The settle timer is the normal path; this one only fires if the page went
    // quiet altogether, which a `while (true)` in setup() does — and which also
    // freezes this timer, so it is a backstop rather than a promise.
    return this.withTimeout(finished, this.startTimeoutMs).catch(() => ({
      outcome: "error" as const,
      detail: "The sketch stopped responding and never reported back.",
    }));
  }

  private withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("timed out")), ms);
      work.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        },
      );
    });
  }

  async stop(): Promise<void> {
    // Freeze, do not tear down. The last frame is the thing the learner was looking
    // at, and a blank panel after Stop reads as "I broke it".
    this.host?.send({ type: "stop" });
  }

  dispose(): void {
    this.host?.destroy();
    this.host = null;
  }
}

/**
 * The real host: an iframe on the page, replaced on every run.
 *
 * Kept here rather than in the runner so the runner never touches the DOM and can
 * be tested under Node — the split `runners.md` asks for.
 */
class IframeSketchHost implements SketchHost {
  private frame: HTMLIFrameElement | null = null;
  private listener: ((event: MessageEvent) => void) | null = null;

  constructor(private readonly surface: HTMLElement) {}

  start(onMessage: (message: SandboxMessage) => void): Promise<void> {
    this.destroy();
    const frame = document.createElement("iframe");
    frame.className = "sketch-frame";
    frame.title = "Your sketch";
    frame.src = SANDBOX_URL;
    // No `sandbox` attribute on purpose — see the note at the top of this file.
    frame.allow = "autoplay";

    return new Promise<void>((resolve) => {
      this.listener = (event: MessageEvent) => {
        if (event.source !== frame.contentWindow) return;
        const data = event.data as (SandboxMessage & { host?: string }) | null;
        if (!data || data.host !== SANDBOX_HOST) return;
        if (data.type === "ready") resolve();
        onMessage(data);
      };
      window.addEventListener("message", this.listener);
      this.frame = frame;
      this.surface.appendChild(frame);
    });
  }

  send(message: HostMessage): void {
    // targetOrigin is our own origin: the frame is same-origin by design, and "*"
    // would post the learner's code to whatever happened to be there instead.
    this.frame?.contentWindow?.postMessage(
      { ...message, host: SANDBOX_HOST },
      window.location.origin,
    );
  }

  destroy(): void {
    if (this.listener) window.removeEventListener("message", this.listener);
    this.listener = null;
    this.frame?.remove();
    this.frame = null;
  }
}

/** Production factory. */
export function createP5Runner(): P5Runner {
  return new P5Runner((surface) => new IframeSketchHost(surface));
}
